import { Location } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideRouter, TitleStrategy, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { GROUP_SERVICE_CONFIG, GroupServiceConfig, IWAH_CONFIG, IwahConfig } from '@iwah/core';
import {
  FORTRESS_CONFIG,
  FortressConfig,
  PermissionCheckService,
  UserPermissionCheckService,
} from '@psu-pennstateit-swe/apis/fortress';
import { SEARCH_CONFIG, SearchConfig } from '@psu-pennstateit-swe/apis/search';
import { ANALYTICS_CONFIG, AnalyticsConfig } from '@psu-pennstateit-swe/utils/analytics';
import {
  CacheInterceptor,
  LoadingEvents,
  REQUEST_TRACING_CONFIG,
  RequestCacheInterceptor,
} from '@psu-pennstateit-swe/utils/browser';
import { CDN_CONFIG, CdnConfig } from '@psu-pennstateit-swe/utils/cdn';
import {
  AUTH_CONFIG_DD,
  AuthConfigWithDiscoveryDoc,
  AuthInterceptor,
  AuthModule,
  AuthService,
  OAuthErrorEvent,
  UserIdRequestTracingInterceptor,
} from '@psu-pennstateit-swe/utils/security';
import { DateAdapter, provideCalendar } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { filter, takeWhile } from 'rxjs';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { IwahTitleStrategy } from './iwah-title.strategy';
import { APP_CONFIG, APP_PROPERTIES, AppConfig, AppProperties } from './properties.model';

const analyticsFactory = (props: AppProperties): AnalyticsConfig => props.analytics;

const appFactory = (props: AppProperties): AppConfig => props.app;

const cdnFactory = (props: AppProperties): CdnConfig => props.cdn;

const fortressFactory = (props: AppProperties): FortressConfig => props.fortress;

const iwahFactory = (props: AppProperties): IwahConfig => props.iwah;

const groupServiceFactory = (props: AppProperties): GroupServiceConfig => props.groupService;

const initializeAppFactory =
  (
    authConfig: AuthConfigWithDiscoveryDoc,
    authService: AuthService,
    loadingEvents: LoadingEvents,
    location: Location,
  ): (() => Promise<void>) =>
  async () => {
    authService.configure(authConfig);

    if (authConfig.showDebugInformation) {
      authService.enableDebug();
    }

    // Listen for error events until a token_received event is emitted
    authService.events
      .pipe(
        takeWhile((event) => event.type !== 'token_received'),
        filter((event): event is OAuthErrorEvent => 'reason' in event),
      )
      .subscribe((event) => {
        document.dispatchEvent(
          new CustomEvent('apperror', {
            bubbles: true,
            detail: event,
          }),
        );
      });

    await authService.loadDiscoveryDocument(authConfig.discoveryDocumentUrl);
    await authService.tryLogin();

    if (authService.isLoggedIn()) {
      authService.setupAutomaticSilentRefresh();
      loadingEvents.triggerAppReady();
    } else {
      authService.initLoginFlow(location.path());
      return new Promise(() => {
        /* This promise will never be settled which prevents the app from loading */
      });
    }
  };

const oauthFactory = (props: AppProperties): AuthConfigWithDiscoveryDoc => props.oauth;
const searchFactory = (props: AppProperties): SearchConfig => props.search;

export const applicationConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(AuthModule),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(routes, withComponentInputBinding(), withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: ANALYTICS_CONFIG, useFactory: analyticsFactory, deps: [APP_PROPERTIES] },
    { provide: APP_CONFIG, useFactory: appFactory, deps: [APP_PROPERTIES] },
    provideAppInitializer(() => {
      const initializerFn = initializeAppFactory(
        inject(AUTH_CONFIG_DD),
        inject(AuthService),
        inject(LoadingEvents),
        inject(Location),
      );
      return initializerFn();
    }),
    provideCalendar({ provide: DateAdapter, useFactory: adapterFactory }),
    { provide: APP_PROPERTIES, useValue: environment.properties },
    { provide: AUTH_CONFIG_DD, useFactory: oauthFactory, deps: [APP_PROPERTIES] },
    { provide: CDN_CONFIG, useFactory: cdnFactory, deps: [APP_PROPERTIES] },
    { provide: FORTRESS_CONFIG, useFactory: fortressFactory, deps: [APP_PROPERTIES] },
    { provide: GROUP_SERVICE_CONFIG, useFactory: groupServiceFactory, deps: [APP_PROPERTIES] },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RequestCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UserIdRequestTracingInterceptor, multi: true },
    { provide: IWAH_CONFIG, useFactory: iwahFactory, deps: [APP_PROPERTIES] },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 4000, horizontalPosition: 'left', verticalPosition: 'bottom' },
    },
    { provide: PermissionCheckService, useClass: UserPermissionCheckService },
    { provide: REQUEST_TRACING_CONFIG, useValue: { applicationId: 'iwa-hub' } },
    { provide: SEARCH_CONFIG, useFactory: searchFactory, deps: [APP_PROPERTIES] },
    { provide: TitleStrategy, useClass: IwahTitleStrategy },
  ],
};
